import axios from "axios";
import { resizeImageForUpload } from "./imageResize";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// ─── Auth header at module scope (Oct 2026) ───────────────────────────
// AuthProvider also registers a request interceptor, but child effects
// run BEFORE parent effects, so the very first calls on a cold page load
// (branding, /auth/me) went out unauthenticated. For branding that meant
// the tenant's sidebar/tab title paint with the neutral platform brand
// for up to 60s. Registering here runs when this module is imported,
// before React mounts.
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("rescue_token");
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// ─── Silent auto-reload on backend redeploy (Feb 2026) ────────────────
// Every /api response carries an `X-App-Build-Id` header. The first
// response we see this session sets the baseline; any later response
// with a different build-id means the backend has redeployed while
// this tab was open and the JS running in the browser is now stale.
// We blow away the SW cache and reload — zero user interaction, zero
// training required. Drivers see a <100ms blink and come back on the
// fresh version.
//
// Why module-scope and not inside AuthProvider's useEffect: the auth
// interceptor race (Feb 2026 iter 49) proved child effects run before
// parent effects. Registering here runs the moment api.js is imported,
// which is before React even mounts.
let _bootBuildId = null;
let _reloadingForBuild = false;
async function _forceFreshReloadForBuildMismatch() {
  if (_reloadingForBuild) return;
  _reloadingForBuild = true;
  try {
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      // Update — never unregister: unregistering destroys this phone's
      // push sign-up, so job alerts silently stopped after every deploy.
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all((regs || []).map((r) => r.update().catch(() => false)));
    }
  } catch { /* best-effort */ }
  try {
    if (typeof window !== "undefined" && "caches" in window && window.caches) {
      const keys = await window.caches.keys();
      await Promise.all((keys || []).map((k) => window.caches.delete(k).catch(() => false)));
    }
  } catch { /* best-effort */ }
  try {
    const base = window.location.href.split("#")[0].split("?")[0];
    window.location.href = `${base}?v=${Date.now()}`;
  } catch {
    window.location.reload();
  }
}
api.interceptors.response.use(
  (response) => {
    try {
      const build = response.headers?.["x-app-build-id"];
      if (build) {
        if (_bootBuildId === null) {
          _bootBuildId = build;
        } else if (build !== _bootBuildId) {
          // Fire-and-forget — we don't want to block the response
          // chain, the page is about to reload anyway.
          _forceFreshReloadForBuildMismatch();
        }
      }
    } catch { /* never let interceptor crash the response */ }
    try {
      // Service-worker offline fallback → tell the UI it's showing saved data.
      const fromCache = response.headers?.["x-sw-fallback"] === "1";
      if (fromCache !== _servedFromCache) {
        _servedFromCache = fromCache;
        window.dispatchEvent(new CustomEvent("tow-offline-data", { detail: fromCache }));
      }
    } catch { /* never let interceptor crash the response */ }
    return response;
  },
  (err) => Promise.reject(err),
);
let _servedFromCache = false;
export const isServedFromCache = () => _servedFromCache;

export const fetchTrucks = () => api.get("/trucks").then((r) => r.data);
export const listTrucks = fetchTrucks;
export const fetchJobs = () => api.get("/jobs").then((r) => r.data);
export const listJobs = (params = {}) => api.get("/jobs", { params }).then((r) => r.data);
export const getJob = (jobId) => api.get(`/jobs/${jobId}`).then((r) => r.data);

// Manual customer tow charge (ex-GST). Set by admin/dispatcher/billing to
// price direct-customer tows or add extras (waiting time, chain hire, etc)
// on top of provider rates. Pass `null` to clear the field.
export const setManualTowCharge = (jobId, amount) =>
  api.patch(`/jobs/${jobId}/manual-tow-charge`, { manual_tow_charge: amount }).then((r) => r.data);
// reGeocodeJob kept server-side but UI removed (June 2026 — user preferred
// fixing address text via Edit; re-pin buttons were overthinking it).
export const reGeocodeJob = (jobId, target = "pickup") =>
  api.patch(`/jobs/${jobId}/re-geocode`, { target }).then((r) => r.data);

// Force a fresh geocode + OSRM round-trip computation for a job whose km
// is blank or flagged for review. Surfaces the fix for AA-cancel-email /
// other email-intake jobs that landed pre-Feb-2026 without km populated.
export const recalculateJobKm = (jobId) =>
  api.post(`/jobs/${jobId}/km/recalculate`).then((r) => r.data);

// Mark km as manually confirmed so the review flag stops nagging.
export const confirmJobKm = (jobId) =>
  api.post(`/jobs/${jobId}/km/confirm`).then((r) => r.data);
export const fetchStats = () => api.get("/stats").then((r) => r.data);
// ── Web push (driver alerts when the app is closed) ─────────────────────
export const fetchVapidPublicKey = () =>
  api.get("/push/vapid-public-key").then((r) => r.data.publicKey);
export const subscribePush = (subscription, truckId, muted = false) =>
  api.post("/push/subscribe", { subscription, truck_id: truckId || null, muted }).then((r) => r.data);
export const sendTestPush = (body) => api.post("/push/test", body).then((r) => r.data);
export const mutePushDevice = (endpoint, muted) =>
  api.post("/push/mute", { endpoint, muted }).then((r) => r.data);
export const fetchSourceBreakdown = (range = "today") =>
  api.get("/stats/source-breakdown", { params: { range } }).then((r) => r.data);
export const createJob = (payload, { force = false } = {}) =>
  api.post(`/jobs${force ? "?force=true" : ""}`, payload).then((r) => r.data);
// Yard-outbound auto-link (Feb 2026) — retro-entry helpers.
// `getYardLinkPreview` is a read-only dry-run of the auto-linker used
// after a retroactive job is created to decide whether to prompt the
// user to link it to a yarded parent.
// `confirmYardLink` commits the link (rego + yard + not already linked).
// `unlinkYard` is the admin escape hatch when an auto-link picked wrong.
export const getYardLinkPreview = (jobId) =>
  api.get(`/jobs/${jobId}/yard-link-preview`).then((r) => r.data);
export const confirmYardLink = (jobId) =>
  api.post(`/jobs/${jobId}/link-yarded`).then((r) => r.data);
export const unlinkYard = (jobId) =>
  api.post(`/jobs/${jobId}/unlink-yarded`).then((r) => r.data);

// Email Intake (Feb 2026 refit): only the manual paste-and-parse flow
// consumes the intake API. The review-queue endpoints (list, unread-count,
// duplicates, bulk-discard, empty-discarded) were removed alongside the
// nuked /intake-inbox page — allowlisted senders auto-create jobs and
// unlisted mail is dropped silently.
export const acceptIntake = (intakeId, payload) =>
  api.post(`/intake/${intakeId}/accept`, payload).then((r) => r.data);
export const discardIntake = (intakeId, reason) =>
  api
    .post(`/intake/${intakeId}/discard${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`)
    .then((r) => r.data);
export const releaseBookedJob = (jobId) => api.post(`/jobs/${jobId}/release-booked`).then((r) => r.data);
export const bookJobForLater = (jobId, bookForIso) =>
  api.post(`/jobs/${jobId}/book-for-later`, { book_for: bookForIso }).then((r) => r.data);

// Belongings release (Feb 2026) — log that an owner collected personal
// items from a stored/wrecked vehicle. Vehicle stays in the yard. Body:
// {collector_name, items, notes?}. Response: full updated job doc.
export const logBelongingsRelease = (jobId, payload) =>
  api.post(`/jobs/${jobId}/belongings-release`, payload).then((r) => r.data);

// Feb 2026 — Vehicle Release Form is now returned as HTML (auto-prints in
// the browser). This helper wraps the axios GET so the Bearer token is
// attached, then opens the HTML in a new tab via a blob URL. Used by
// PrintReleaseFormButton and the Belongings pickup dialog.
export const openReleaseFormPrint = async (jobId, action = "vehicle_to_owner") => {
  const r = await api.get(`/jobs/${jobId}/release-form`, {
    params: { action },
    responseType: "text",
  });
  const blob = new Blob([r.data], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
export const createTruck = (payload) => api.post("/trucks", payload).then((r) => r.data);
export const assignJob = (jobId, payload) =>
  api.post(`/jobs/${jobId}/assign`, payload).then((r) => r.data);
export const returnJobToQueue = (jobId, payload = null) =>
  api.post(`/jobs/${jobId}/return-to-queue`, payload || {}).then((r) => r.data);
export const updateJobStatus = (jobId, status, photosSkipReason = null) =>
  api.put(`/jobs/${jobId}/status`, {
    status,
    ...(photosSkipReason ? { photos_skip_reason: photosSkipReason } : {}),
  }).then((r) => r.data);
// Aug 2026 driver-flow refit: explicit "I've finished it in AA / FR — CLOSE"
// tap. Writes a per-tap `completed_at` so pollers can't collapse multiple
// AA/FR closes onto the same `now_iso` (the 21:17 bug). Idempotent server-side.
export const driverConfirmedClose = (jobId) =>
  api.post(`/jobs/${jobId}/driver-confirmed-close`).then((r) => r.data);
export const updateJob = (jobId, payload) =>
  api.put(`/jobs/${jobId}`, payload).then((r) => r.data);
export const updateTruck = (truckId, payload) =>
  api.put(`/trucks/${truckId}`, payload).then((r) => r.data);
export const updateTruckStatus = (truckId, status) =>
  api.patch(`/trucks/${truckId}/status`, { status }).then((r) => r.data);
export const deleteJob = (jobId) => api.delete(`/jobs/${jobId}`).then((r) => r.data);
export const clearCancelledJobs = () => api.post("/jobs/clear-cancelled").then((r) => r.data);
// "Cancelled on Route" — customer cancelled while the truck was on the way.
// charge=true → job marked completed + flagged for invoicing; false → cancelled.
export const cancelJobOnRoute = (jobId, charge, reason) =>
  api.post(`/jobs/${jobId}/cancel-on-route`, { charge, reason: reason || null }).then((r) => r.data);
// Driver taps Acknowledge on the "AA Cancelled On-Route" modal — frees
// their queue slot so they can move to the next job. Dispatcher still
// needs to decide Charge / No Charge via cancelJobOnRoute above.
export const ackAaCancelOnRoute = (jobId, note = null) =>
  api.post(`/jobs/${jobId}/ack-cancel-on-route`, { note }).then((r) => r.data);
// Put a cancelled job back into the pending dispatch queue (data intact).
export const restoreCancelledJob = (jobId) =>
  api.post(`/jobs/${jobId}/restore`).then((r) => r.data);
// Admin: scan history for missed Parkers Rd storage jobs, back-date clocks.
export const backfillParkersStorage = (dryRun = true, jobIds = null) =>
  api.post("/yards/backfill-parkers-storage", { dry_run: dryRun, ...(jobIds ? { job_ids: jobIds } : {}) }).then((r) => r.data);
export const clearCompletedJobs = () => api.post("/jobs/clear-completed").then((r) => r.data);
// Archive workflow (billing / admin only) — soft-hides invoiced jobs from
// operational views while preserving data + photos.
export const archiveJob = (jobId) =>
  api.post(`/jobs/${jobId}/archive`).then((r) => r.data);
export const setJobInvoiced = (jobId, invoiced) =>
  api.post(`/jobs/${jobId}/invoiced`, { invoiced }).then((r) => r.data);
// Set the billing state by hand. Only Non Chargeable / To Invoice / Invoice
// Sent / Written Off are settable — "Paid" and "Signed Over" are facts the
// app derives. Pass null to go back to the derived answer.
export const setBillingState = (jobId, state) =>
  api.post(`/jobs/${jobId}/billing-state`, { state }).then((r) => r.data);

export const setBillingStateBulk = (jobIds, state) =>
  api.post(`/jobs/billing-state-bulk`, { job_ids: jobIds, state }).then((r) => r.data);
// Charge a different yard-storage figure from the one the clock worked out
// (the weekend we didn't charge for). Pass null to go back to days × rate.
export const setStorageCharge = (jobId, amount, reason) =>
  api.put(`/jobs/${jobId}/storage-charge`, { amount, reason }).then((r) => r.data);
// Who the invoice is addressed to in Xero/MYOB, when that isn't the name on
// the job. Office decision, not the dispatcher's.
export const setBillTo = (jobId, name) =>
  api.put(`/invoice-export/jobs/${jobId}/bill-to`, { name }).then((r) => r.data);
export const knownPayers = () =>
  api.get("/invoice-export/payers").then((r) => r.data.payers);
export const setJobTowAuthority = (jobId, towAuthorityNumber) =>
  api.post(`/jobs/${jobId}/tow-authority`, { tow_authority_number: towAuthorityNumber }).then((r) => r.data);
export const unarchiveJob = (jobId) =>
  api.post(`/jobs/${jobId}/unarchive`).then((r) => r.data);
export const archiveJobsBulk = (jobIds) =>
  api.post("/jobs/archive-bulk", { job_ids: jobIds }).then((r) => r.data);

// ── Admin: Danger Zone ─────────────────────────────────────────────────
export const purgeTestData = () =>
  api.post("/admin/purge-test-data").then((r) => r.data);
export const fetchPhotoRetention = (days) =>
  api.get("/admin/photo-retention", { params: days ? { days } : {} }).then((r) => r.data);
export const savePhotoRetention = (enabled, days) =>
  api.put("/admin/photo-retention", { enabled, days }).then((r) => r.data);
export const runPhotoRetention = () =>
  api.post("/admin/photo-retention/run").then((r) => r.data);
// Duplicate-WO cleanup + unique-index rebuild. Two-step by design —
// dry-run first (report only), then wet-run (delete + build index).
export const purgeDuplicateWos = (dryRun = true) =>
  api.post(`/admin/purge-duplicate-wos?dry_run=${dryRun}`).then((r) => r.data);

// Copy disk-only photos into MongoDB GridFS so they survive future
// redeploys. `dryRun=true` counts what WOULD migrate without touching
// anything; `dryRun=false` performs the migration. Idempotent — safe
// to run repeatedly.
export const migratePhotosToGridfs = (dryRun = true) =>
  api.post(`/admin/migrate-photos-to-gridfs?dry_run=${dryRun}`).then((r) => r.data);

// ---------- YARD STOCK ----------
// Lightweight list of yards configured on the impound config. Feeds the
// Yard Stock page's filter tabs so slugs never drift from the DB.
export const listConfiguredYards = () =>
  api.get("/yards/list").then((r) => r.data);

export const listYardStock = ({ yard = "all", q = "", inYardOnly = false } = {}) => {
  const params = new URLSearchParams();
  if (yard && yard !== "all") params.set("yard", yard);
  if (q) params.set("q", q);
  if (inYardOnly) params.set("in_yard_only", "true");
  const qs = params.toString();
  return api.get(`/yards/stock${qs ? `?${qs}` : ""}`).then((r) => r.data);
};

// Undo an accidental DVA release — puts the vehicle back on the live
// yard-stock list. Server-side guards ensure this only works on rows
// that are DVA-released AND not NTS-signed-over.
export const restoreYardJobToYard = (jobId, payload = {}) =>
  api.post(`/yards/jobs/${jobId}/restore-to-yard`, payload).then((r) => r.data);

// Retro reverse-linker (Feb 2026 A63707545 hole-plug): glue a yarded
// parent to a completed on-tow-out job that was created BEFORE the
// parent's drop-off was corrected to a yard address. Server searches
// for a matching completed outbound (same rego, pickup at same yard,
// closed within a 1h grace of the parent's completed_at) and stops
// the parent's storage clock at the outbound's completed_at.
// Response shape:
//   { linked: true,  outbound_job_id, outbound_rego, outbound_completed_at, match_reason }
//   { linked: false, detail: "No matching completed on-tow-out job..." }
export const relinkYardOutbound = (jobId) =>
  api.post(`/yards/jobs/${jobId}/relink-outbound`).then((r) => r.data);

export const departYard = (jobId, payload) =>
  api.post(`/jobs/${jobId}/depart-yard`, payload).then((r) => r.data);

// New-job guard: is this rego currently in a yard with a live storage clock?
export const checkRegoInYard = (rego) =>
  api.get("/yards/check-rego", { params: { rego } }).then((r) => r.data);

// New-job guard (Feb 2026): is this rego currently on an active police impound?
// Blocks overlapping tow-job creation to prevent double-billing.
export const checkRegoInImpound = (rego) =>
  api.get("/impounds/check-rego", { params: { rego } }).then((r) => r.data);

// Sign a yarded vehicle over to NTS (owner abandoned / uninsured).
// Snapshots the write-off amount and stops the storage clock. Vehicle
// then appears on the NTS Owned in Lieu page for the billing user to reconcile.
export const releaseJobToNts = (jobId, payload) =>
  api.post(`/jobs/${jobId}/release-to-nts`, payload).then((r) => r.data);

// DVA (auction) release — vehicle sold by a 3rd-party auction house.
// Stops the storage clock, snapshots the purchaser details, and streams
// back the release-form PDF as a Blob (no dollar figures on the doc).
// The row stays on the yard list for 14 days after release, then
// auto-archives out. Idempotent: subsequent calls just re-issue the PDF.
export const releaseYardJobDva = (jobId, payload) =>
  api
    .post(`/yards/jobs/${jobId}/release-dva`, payload, { responseType: "blob" })
    .then((r) => r.data);

// Reprint a previously issued DVA release form (no state change).
export const fetchDvaReleasePdf = (jobId) =>
  api
    .get(`/yards/jobs/${jobId}/dva-release-pdf`, { responseType: "blob" })
    .then((r) => r.data);


// ── Police Impounds ─────────────────────────────────────────────────────
export const fetchImpoundConfig = () =>
  api.get("/integration/police-impound/config").then((r) => r.data);
export const updateImpoundConfig = (patch) =>
  api.put("/integration/police-impound/config", patch).then((r) => r.data);
export const quoteImpound = (payload) =>
  api.post("/integration/police-impound/quote", payload).then((r) => r.data);
export const listImpounds = (params = {}) =>
  api.get("/impounds", { params }).then((r) => r.data);

// ── AA Tow Pricing (member roadside quotes) ──────────────────────────────
export const fetchAaTowPricing = () =>
  api.get("/integration/aa-tow/pricing").then((r) => r.data);
export const updateAaTowPricing = (patch) =>
  api.put("/integration/aa-tow/pricing", patch).then((r) => r.data);
export const quoteAaTow = (payload) =>
  api.post("/integration/aa-tow/quote", payload).then((r) => r.data);

// Shared helper for FR / SN quote (same payload shape as AA; service_type optional).
export const quoteSnTow = (payload) =>
  api.post("/integration/sn-tow/quote", payload).then((r) => r.data);

// ── Manual Tow Pricing (Tow Calc page) ─────────────────────────────────
export const fetchManualTowPricing = () =>
  api.get("/pricing/manual").then((r) => r.data);
export const updateManualTowPricing = (patch) =>
  api.patch("/pricing/manual", patch).then((r) => r.data);
export const quoteManualTow = (payload) =>
  api.post("/pricing/manual/quote", payload).then((r) => r.data);

// ── First Rescue (ServiceNow) Tow Pricing — separate rate card. ──────────
// Different base for tow vs jumpstart, separate public-holiday surcharge that
// stacks ON TOP of the after-hours rate, plus a per-extra-km FAF.
export const fetchSnTowPricing = () =>
  api.get("/integration/sn-tow/pricing").then((r) => r.data);
export const updateSnTowPricing = (patch) =>
  api.put("/integration/sn-tow/pricing", patch).then((r) => r.data);

// ── Business info + impound owner letters ────────────────────────────────
export const fetchBusinessInfo = () =>
  api.get("/business-info").then((r) => r.data);
export const updateBusinessInfo = (patch) =>
  api.put("/business-info", patch).then((r) => r.data);

export const fetchOnboardingState = () =>
  api.get("/onboarding/state").then((r) => r.data);
export const saveOnboardingEssentials = (payload) =>
  api.post("/onboarding/essentials", payload).then((r) => r.data);
export const fetchSampleDataStatus = () =>
  api.get("/onboarding/sample-data").then((r) => r.data);
export const clearSampleData = () =>
  api.delete("/onboarding/sample-data").then((r) => r.data);

// Geocoding helpers for the yard / depot map picker.
export const geocodeAddress = (address) =>
  api.get("/geocode/resolve", { params: { address } }).then((r) => r.data);
// Pasted pin drop (coords / maps link) → exact coords + street address.
export const resolvePinDrop = (text) =>
  api.get("/geocode/pin", { params: { text } }).then((r) => r.data);
export const reverseGeocode = (lat, lng) =>
  api.get("/geocode/reverse", { params: { lat, lng } }).then((r) => r.data);
export const previewOwnerLetter = (jobId) =>
  api.get(`/impounds/${jobId}/owner-letter/preview`).then((r) => r.data);
export const saveOwnerLetter = (jobId, payload = {}) =>
  api.post(`/impounds/${jobId}/owner-letter`, payload).then((r) => r.data);
export const listOwnerLetters = (jobId) =>
  api.get(`/impounds/${jobId}/owner-letter/history`).then((r) => r.data);
export const fetchOwnerLetter = (letterId) =>
  api.get(`/impound-letters/${letterId}`).then((r) => r.data);

// ── Tow-job storage fees ─────────────────────────────────────────────────
// Party-based storage rate table (rates INCL 15% GST; quotes convert to ex-GST).
export const fetchStorageRates = () => api.get("/storage-rates").then((r) => r.data);
export const saveStorageRates = (categories) =>
  api.put("/storage-rates", { categories }).then((r) => r.data);
// Manual rate override per yarded job. category: null=auto, "default"=impound rate, else table key.
export const setJobStorageRateCategory = (jobId, category) =>
  api.patch(`/jobs/${jobId}/storage-rate`, { category }).then((r) => r.data);
export const fetchJobStorageQuote = (jobId) =>
  api.get(`/jobs/${jobId}/storage-quote`).then((r) => r.data);
export const setJobStorageYard = (jobId, yardSlug) =>
  api.put(`/jobs/${jobId}/storage-yard`, { storage_yard: yardSlug }).then((r) => r.data);

// NTS Inventory — vehicles signed over to NTS after release-to-NTS write-off.
// `disposed` filter: "all" | "true" | "false" (still in inventory).
export const listNtsImpounds = (disposed = "all") =>
  api.get("/nts-impounds", { params: { disposed } }).then((r) => r.data);

// Month-end P&L PDF for NTS-owned-in-lieu vehicles. `month` is "YYYY-MM".
// Returns a token-stamped URL so the dispatcher can click → open in a new
// tab. Backend accepts the JWT via `?t=<token>` (same pattern as paperwork).
export const ntsPlReportUrl = (month) =>
  buildAuthedUrl(`/api/admin/nts-pl-report?month=${encodeURIComponent(month)}`);

// Record disposal of an NTS-owned vehicle (sold / scrapped / donated / kept).
// For "sold", `sold_to` (buyer name) and `sold_amount` (NZD) are required.
export const disposeNtsVehicle = (jobId, payload) =>
  api.post(`/nts-impounds/${jobId}/dispose`, payload).then((r) => r.data);

// Admin-only revert.
export const undoDisposeNtsVehicle = (jobId) =>
  api.post(`/nts-impounds/${jobId}/undo-dispose`).then((r) => r.data);

// Reverse a whole sign-over (not just the disposal step). Dispatchers get a
// 48-hour window; after that the backend requires an admin.
export const undoNtsSignover = (jobId, reason) =>
  api.post(`/nts-impounds/${jobId}/undo-signover`, { reason }).then((r) => r.data);

// Edit an in-progress impound (admin + dispatcher). Backend rejects edits to
// released or NTS-signed-over records to protect the snapshotted quote.
export const updateImpound = (jobId, payload) =>
  api.patch(`/impounds/${jobId}`, payload).then((r) => r.data);

// NTS sign-over paperwork — list / upload / delete.
export const listNtsPaperwork = (jobId) =>
  api.get(`/nts-impounds/${jobId}/paperwork`).then((r) => r.data);

export const uploadNtsPaperwork = (jobId, file, caption = "") => {
  const fd = new FormData();
  fd.append("file", file);
  if (caption) fd.append("caption", caption);
  return api
    .post(`/nts-impounds/${jobId}/paperwork`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

export const deleteNtsPaperwork = (jobId, docId) =>
  api.delete(`/nts-impounds/${jobId}/paperwork/${docId}`).then((r) => r.data);

// Build a publicly clickable URL for an attached paperwork file. The JWT
// is passed as `?t=<token>` because <a>/<img>/<iframe> tags can't attach
// Authorization headers — the backend accepts the same JWT either way.
export const buildAuthedUrl = (relUrl) => {
  if (!relUrl) return "";
  const token = typeof window !== "undefined" ? localStorage.getItem("rescue_token") : null;
  const base = BACKEND_URL || "";
  const sep = relUrl.includes("?") ? "&" : "?";
  return `${base}${relUrl}${token ? `${sep}t=${token}` : ""}`;
};
export const createImpound = (payload) =>
  api.post("/impounds", payload).then((r) => r.data);
export const releaseImpound = (jobId, payload) =>
  api.post(`/impounds/${jobId}/release`, payload).then((r) => r.data);
// Police-authorised early release — prorates storage to actual days held.
export const earlyReleaseImpound = (jobId, payload) =>
  api.post(`/impounds/${jobId}/early-release`, payload).then((r) => r.data);
// Part payments — owner pays impound charges off before release.
export const recordImpoundPartPayment = (jobId, payload) =>
  api.post(`/impounds/${jobId}/part-payment`, payload).then((r) => r.data);
export const deleteImpoundPartPayment = (jobId, paymentId) =>
  api.delete(`/impounds/${jobId}/part-payment/${paymentId}`).then((r) => r.data);
export const releaseImpoundToNts = (jobId, payload) =>
  api.post(`/impounds/${jobId}/release-to-nts`, payload).then((r) => r.data);
// Sep 2026 — merge two impound rows that are duplicates for the same rego.
// Keeper wins on scalar fields; drop-row's photos + part_payments concat in;
// linked owner-letters and sent_emails repoint to keeper; drop row is
// hard-deleted. Refuses when regos differ or drop row is already released.
export const mergeImpoundDuplicate = (keepId, dropId) =>
  api.post(`/impounds/${keepId}/merge/${dropId}`).then((r) => r.data);
// Deep-link helper — returns { ok, url, label, provider } so the driver tablet
// can open the job in the partner app/portal they're already signed into.
// Feb 2026 refit — the URL now always points at the partner's HOME screen,
// never a per-job deep-link. See backend `/partner-link` docstring.
export const fetchJobPartnerLink = (jobId) =>
  api.get(`/jobs/${jobId}/partner-link`).then((r) => r.data);
// Trail marker — driver tapped "Open Partner App". Fire-and-forget.
// Rate-limited server-side to once per 60 s per job; safe to call on
// every tap without flooding the dispatcher timeline.
export const markJobPartnerOpened = (jobId) =>
  api.post(`/jobs/${jobId}/partner-opened`).then((r) => r.data);
export const deleteTruck = (truckId) => api.delete(`/trucks/${truckId}`).then((r) => r.data);

// ─── Password reset + admin user management ──────────────────────────────
export const forgotPassword = (email) =>
  api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, new_password) =>
  api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);
export const listUsers = () => api.get("/users").then((r) => r.data);
export const adminResetPassword = (userId, new_password) =>
  api.post(`/users/${userId}/reset-password`, { new_password }).then((r) => r.data);
export const usersMeta = () => api.get("/users/meta").then((r) => r.data);
export const createUser = (body) => api.post("/users", body).then((r) => r.data);
export const updateUserPermissions = (userId, permissions) =>
  api.patch(`/users/${userId}/permissions`, { permissions }).then((r) => r.data);
export const updateUserProfile = (userId, body) =>
  api.patch(`/users/${userId}/profile`, body).then((r) => r.data);

// ─── Invoice export (Xero / MYOB file) ───────────────────────────────────
export const invoiceExportSettings = () =>
  api.get("/invoice-export/settings").then((r) => r.data);
export const saveInvoiceExportSettings = (body) =>
  api.put("/invoice-export/settings", body).then((r) => r.data);
export const pendingInvoices = (fmt) =>
  api.get("/invoice-export/pending", { params: { fmt } }).then((r) => r.data);
export const runInvoiceExport = (job_ids, format) =>
  api.post("/invoice-export/run", { job_ids, format }).then((r) => r.data);
export const invoiceBatches = () =>
  api.get("/invoice-export/batches").then((r) => r.data);
export const downloadInvoiceBatch = (id) =>
  api.get(`/invoice-export/batches/${id}/file`).then((r) => r.data);
export const undoInvoiceBatch = (id) =>
  api.post(`/invoice-export/batches/${id}/undo`).then((r) => r.data);
export const moveJob = (jobId, direction) =>
  api.post(`/jobs/${jobId}/move`, { direction }).then((r) => r.data);
export const reorderTruckQueue = (truckId, jobIds) =>
  api.post(`/trucks/${truckId}/queue/reorder`, { job_ids: jobIds }).then((r) => r.data);
export const fetchTruckQueue = (truckId) =>
  api.get(`/trucks/${truckId}/queue`).then((r) => r.data);

// On-call / after-hours
export const getOncallStatus = () => api.get("/oncall/status").then((r) => r.data);
export const assignOncallTruck = (truckId) =>
  api.post("/oncall/assign-truck", { truck_id: truckId }).then((r) => r.data);
export const setOncallConfig = (cfg) => api.put("/oncall/config", cfg).then((r) => r.data);
export const listClaimableJobs = () =>
  api.get("/oncall/available-jobs").then((r) => r.data);
export const claimJob = (jobId, truckId) =>
  api.post(`/jobs/${jobId}/claim`, { truck_id: truckId }).then((r) => r.data);

export const fetchTgHealth = () =>
  api.get("/integration/telematics-guru/health").then((r) => r.data);

export const uploadJobPhoto = async (jobId, file, caption) => {
  // Client-side resize to ~1600px longest edge — cuts 4-12 MB phone shots
  // down to ~300-600 KB with no visible loss for panel-damage evidence.
  // Massive win on 4G/rural cell where drivers actually upload from.
  // NOTE: statically imported (top of file) — a lazy chunk can't be fetched
  // offline, which silently broke the offline photo queue on first use.
  const resized = await resizeImageForUpload(file);
  const fd = new FormData();
  fd.append("file", resized);
  if (caption) fd.append("caption", caption);
  return api.post(`/jobs/${jobId}/photos`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    // A stalled upload on one bar used to hang with no ceiling anywhere in
    // the stack, jamming the offline flush loop behind it. Treated as a
    // network error, so the photo stays queued and goes on the next pass.
    timeout: 20000,
  }).then((r) => r.data);
};
export const fetchJobPhotos = (jobId) =>
  api.get(`/jobs/${jobId}/photos`).then((r) => r.data.photos);
export const deleteJobPhoto = (jobId, photoId) =>
  api.delete(`/jobs/${jobId}/photos/${photoId}`).then((r) => r.data);
// Rotates the stored pixels (both the disk and GridFS copies), so the
// straightened photo is what the insurer's PDF and email attachments carry
// too — not just what the gallery displays.
export const rotateJobPhoto = (jobId, photoId, turns = 1) =>
  api.post(`/jobs/${jobId}/photos/${photoId}/rotate`, null, { params: { turns } })
    .then((r) => r.data);

// Downloads ZIP of all photos + job-summary.txt as a Blob (auth header attached
// via axios interceptor; cannot use a plain href because that strips the token).
export const downloadJobPhotoBundle = (jobId) =>
  api
    .get(`/jobs/${jobId}/photos/bundle`, { responseType: "blob" })
    .then((r) => {
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : `Photos-${jobId}.zip`;
      return { blob: r.data, filename };
    });
